













import { computed, defineComponent, ref } from "@vue/composition-api";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { CallPrice } from "@scrile/api-provider/dist/api/CallPriceProvider";
import { MessagePrices } from "@scrile/api-provider/dist/api/MessagePricesProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import config from "@/config";
import { FormValues } from "@/types";
import PriceSettingsView from "./components/PriceSettingsView.vue";

export default defineComponent({
  name: "PagePriceSettings",
  components: { PriceSettingsView },
  setup(_, { root }) {
    const { authUserStrict, authorities, isAdmin } = useAuthUser();
    const loading = ref(false);
    const callPrice = ref<CallPrice | null>(null);
    const messagePrice = ref<MessagePrices | null>(null);
    const callOptions = ref<CallOptions | null>(null);
    const callPaymentPackages = ref<PaymentPackage[]>([]);
    const messagePaymentPackages = ref<PaymentPackage[]>([]);

    const callValues = ref<FormValues>({
      callPricePackageId: null,
      incomingCallsEnabled: true,
    });

    const messageValues = ref<FormValues>({
      perSentPriceId: "0",
      subscriberPerSentPriceId: "0",
      perSentWithMediaPriceId: "0",
      subscriberPerSentWithMediaPriceId: "0",
      perSentWithAudioPriceId: "0",
      subscriberPerSentWithAudioPriceId: "0",
    });

    const checkedCallOptionsAuthorities = computed(() => {
      return checkAuthorities(authorities.value || [], ["updateMyCallOptions"]);
    });
    const checkedCallAuthorities = computed(() => {
      return checkAuthorities(authorities.value || [], ["updateMyCallPrice"]);
    });
    const checkedMessageAuthorities = computed(() => {
      return checkAuthorities(authorities.value || [], ["updateMyMessagePrice"]);
    });

    const { paymentPackages, getPaymentPackages: findAllPackages } = usePaymentPackages([
      ...(config.featureCalling && (checkedCallAuthorities.value || checkedCallOptionsAuthorities.value)
        ? [PaymentPackageEnum.PER_MINUTE_CALL]
        : []),
      ...(config.featurePaidSentMessage && checkedMessageAuthorities.value
        ? [PaymentPackageEnum.PER_SENT_MESSAGE]
        : []),
    ]);

    const getCallPrice = async () => {
      if (config.featureCalling && checkedCallAuthorities.value && !isAdmin.value) {
        callPrice.value = (await providers.CallPriceProvider.find({ userIds: [authUserStrict.value.id] }))[0] ?? null;
        callValues.value = { ...callValues.value, callPricePackageId: callPrice.value?.perMinutePriceId || "0" };
      }
    };

    const getMessagePrice = async () => {
      if (config.featurePaidSentMessage && checkedMessageAuthorities.value && !isAdmin.value) {
        messagePrice.value =
          (await providers.MessagePricesProvider.find({ userIds: [authUserStrict.value.id] }))[0] ?? null;
        messageValues.value = {
          ...messageValues.value,
          perSentPriceId: messagePrice.value?.perSentPriceId || "0",
          perSentWithMediaPriceId: messagePrice.value?.perSentWithMediaPriceId || "0",
          perSentWithAudioPriceId: messagePrice.value?.perSentWithAudioPriceId || "0",
          subscriberPerSentPriceId: messagePrice.value?.subscriberPerSentPriceId || "0",
          subscriberPerSentWithMediaPriceId: messagePrice.value?.subscriberPerSentWithMediaPriceId || "0",
          subscriberPerSentWithAudioPriceId: messagePrice.value?.subscriberPerSentWithAudioPriceId || "0",
        };
      }
    };

    const getPaymentPackages = async () => {
      if (isAdmin.value) return;
      await findAllPackages();
      callPaymentPackages.value = paymentPackages.value.filter(p => p.type === PaymentPackageEnum.PER_MINUTE_CALL);
      messagePaymentPackages.value = paymentPackages.value.filter(p => p.type === PaymentPackageEnum.PER_SENT_MESSAGE);
    };

    const getCallOptions = async () => {
      if (config.featureCalling && checkedCallOptionsAuthorities.value) {
        callOptions.value =
          (await providers.CallOptionsProvider.find({ userIds: [authUserStrict.value.id] })).find(
            i => i.userId === authUserStrict.value.id
          ) ?? null;
        callValues.value = { ...callValues.value, incomingCallsEnabled: callOptions.value?.enabled ?? true };
      }
    };

    const updateCallPrice = async () => {
      if (
        config.featureCalling &&
        checkedCallAuthorities.value &&
        callValues.value.incomingCallsEnabled &&
        !isAdmin.value
      ) {
        callPrice.value = await providers.CallPriceProvider.update({
          data: { userId: authUserStrict.value.id, perMinutePriceId: callValues.value.callPricePackageId || "0" },
        });
      }
    };

    const updateMessagePrice = async () => {
      if (config.featurePaidSentMessage && checkedMessageAuthorities.value && !isAdmin.value) {
        messagePrice.value = await providers.MessagePricesProvider.update({
          data: {
            userId: authUserStrict.value.id,
            ...messageValues.value,
          },
        });
      }
    };

    const updateCallOptions = async () => {
      if (config.featureCalling && checkedCallOptionsAuthorities.value) {
        const data = { userId: authUserStrict.value.id, enabled: callValues.value.incomingCallsEnabled };
        callOptions.value = await providers.CallOptionsProvider.update({ data });
      }
    };

    const onSubmit = async (cb: Function) => {
      try {
        await Promise.all([updateCallPrice(), updateMessagePrice(), updateCallOptions()]);
        root.$router.back();
      } finally {
        cb();
      }
    };

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([getCallPrice(), getCallOptions(), getPaymentPackages(), getMessagePrice()]);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      loading,
      callPaymentPackages,
      messagePaymentPackages,
      callValues,
      messageValues,
      isAdmin,
      onSubmit,
    };
  },
});
